import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { TextField, CustomButton } from "../../../v2/MUI_custom_component";
import { toCapitalized } from '../../../v2/Utils/Capitalize';
import {
  validateName,
  nameInputValidation,
} from '../../Common/Validation/nameValidation';
import {
  validateMobile,
  mobileInputValidation,
} from '../../Common/Validation/mobileValidation';
import { toast } from "react-toastify";
import { sendOTPAction, verifyOTPAction } from "../../Auth/Register/API/Action";
import { encryptPassword } from "../../../v2/Utils/Encryption/NodeForgeEncryption";
import { OTP_PURPOSE_ENUM } from "../../Auth/Enum";
import OTPVerificationComponent from "../../Common/OTPVerification/OTPVerificationComponent";
import HumbeeModal from "../../../v2/MUI_custom_component/Modal/HumbeeModal";
import { AddFirmSvg } from "../../Illustrration/components/AddFirmSvg";
import { RoleAddtionAction } from "../../FirmOnboarding/API/Action";
import { canOnboardAction, initiateDigiLocker } from "../API/Action";
import { DigiLockerKycModal } from "./DigiLockerKycModal";
import AdharOCR from "./Aadhar Flow/AdharOCR";
import DigiLockerReview from "./Digilocker Flow/DigiLockerReview";
import {
  buildKycFromKycResponse,
  buildKycFromManual,
} from "../Redux/kycDetailsBuilders";

const UserDetailsStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    selectedRole,
    manufacturer,
    userDetails = { name: "", mobileNumber: "" },
    kycDetails,
  } = useSelector((state) => state.onboardBuyerReducer);
  const selectedSupplier = useSelector(
    (state) => state.entityReducer.selectedSupplier,
  );
  const [errors, setErrors] = useState({ name: "", mobileNumber: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [digiLockerOpen, setDigiLockerOpen] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [showAadhaarOcr, setShowAadhaarOcr] = useState(false);
  const [mrmSubmitting, setMrmSubmitting] = useState(false);
  const [mrmPrompt, setMrmPrompt] = useState({
    open: false,
    message: "",
    companyId: null,
    regionRoleId: null,
  });

  const isInfluencerRole = selectedRole?.region_role_id == null;
  const roleName = selectedRole?.display_name
    ? toCapitalized(selectedRole.display_name)
    : "";

  const setField = (field, value) =>
    dispatch({ type: "SET_USER_DETAIL_FIELD", payload: { field, value } });

  const handleNameChange = (e) => {
    setField("name", nameInputValidation(e.target.value));
    setErrors((prev) => ({ ...prev, name: "" }));
  };

  const handleMobileChange = (e) => {
    setField("mobileNumber", mobileInputValidation(e.target.value));
    setErrors((prev) => ({ ...prev, mobileNumber: "" }));
  };

  const isValid =
    userDetails.mobileNumber.length === 10 &&
    (isInfluencerRole || userDetails.name.trim().length > 0);

  const buildOtpSendPayload = () => ({
    mobile_number: encryptPassword(userDetails.mobileNumber),
    purpose: OTP_PURPOSE_ENUM.ONBOARD_BUYER,
  });

  const handleContinue = async () => {
    const mobileValidation = validateMobile(userDetails.mobileNumber);
    const nameValidation = isInfluencerRole
      ? { valid: true, message: "" }
      : validateName(userDetails.name);

    setErrors({
      name: nameValidation.valid ? "" : nameValidation.message,
      mobileNumber: mobileValidation.valid ? "" : mobileValidation.message,
    });

    if (!nameValidation.valid || !mobileValidation.valid) return;

    setIsSubmitting(true);
    const res = await canOnboardAction({
      mobile_number: encryptPassword(userDetails.mobileNumber),
      manufacturer_id:
        manufacturer?.manufacturer_id ?? Number(selectedSupplier),
      firm_role_id: selectedRole?.firm_role_id,
    });

    if (res?.error) {
      setIsSubmitting(false);
      toast.error(res.message || "Something went wrong");
      return;
    }

    if (!!res?.data?.use_mrm_exists) {
      setIsSubmitting(false);
      setMrmPrompt({
        open: true,
        message: res.data.prompted_message,
        companyId: res.data.company_id,
        regionRoleId: res.data.region_role_id ?? null,
      });
      return;
    }

    const otpRes = await sendOTPAction(buildOtpSendPayload());
    setIsSubmitting(false);
    if (otpRes?.error) {
      toast.error(otpRes.message || "Failed to send OTP");
      return;
    }
    toast.success(otpRes?.message);
    setOtpSent(true);
  };

  const handleVerifyOtp = async (otp) => {
    const res = await verifyOTPAction({
      mobile_number: encryptPassword(userDetails.mobileNumber),
      otp: encryptPassword(otp),
      purpose: OTP_PURPOSE_ENUM.ONBOARD_BUYER,
    });
    if (res?.error) return { message: res.message };
    dispatch({ type: "SET_VFC_TOKEN", payload: res.data?.vfc_token });
    return { data: res.data, message: res.message };
  };

  const handleResendOtp = async () => {
    const { error, message } = await sendOTPAction(buildOtpSendPayload());
    return { success: !error, message };
  };

  const handleOtpVerified = () => {
    setOtpSent(false);
    if (isInfluencerRole) {
      setDigiLockerOpen(true);
      return;
    }
    dispatch({ type: "SET_ONBOARD_ACTIVE_STEP", payload: 2 });
  };

  const closeMrmPrompt = () =>
    setMrmPrompt((prev) => ({ ...prev, open: false }));

  const handleRoleAddition = async () => {
    setMrmSubmitting(true);
    // eslint-disable-next-line no-unused-vars
    const res = await RoleAddtionAction({
      manufacturer_id:
        manufacturer?.manufacturer_id ?? Number(selectedSupplier),
      firm_role_id: selectedRole?.firm_role_id,
      company_id: mrmPrompt.companyId,
      region_role_map_id: mrmPrompt?.regionRoleId,
    });
    setMrmSubmitting(false);

    closeMrmPrompt();
    if (res?.error) {
      toast.error(res.message || "Unable to add role");
      return;
    }
    navigate("/onboard-buyer-success", {
      state: { message: res.message },
    });
  };

  const handleKycContinue = async (method) => {
    if (method === "AADHAAR") {
      setDigiLockerOpen(false);
      setShowAadhaarOcr(true);
      return;
    }
    setKycSubmitting(true);
    const res = await initiateDigiLocker(userDetails.mobileNumber);
    setKycSubmitting(false);

    if (res?.error || !res?.data?.redirect_url) {
      toast.error(res?.message || "Failed to start DigiLocker verification");
      return;
    }
    sessionStorage.setItem(
      "onboard_buyer_digilocker",
      JSON.stringify({
        flow: "ONBOARD_BUYER",
        method: "DIGILOCKER",
        session_id: res.data.session_id,
        mobileNumber: userDetails.mobileNumber,
        name: userDetails.name,
        selectedRole,
        manufacturerId:
          manufacturer?.manufacturer_id ?? Number(selectedSupplier),
        supplierId: selectedSupplier,
        ts: Date.now(),
      }),
    );

    window.location.href = res.data.redirect_url;
  };

  const handleKycVerifiedContinue = () => {
    dispatch({ type: "SET_ONBOARD_ACTIVE_STEP", payload: 2 });
  };
  const handleAadhaarSave = (data, source) => {
    const kyc =
      source === "OCR"
        ? buildKycFromKycResponse(data, "OCR")
        : buildKycFromManual(data);
    dispatch({ type: "SET_KYC_DETAILS", payload: kyc });
    dispatch({ type: "SET_ONBOARD_ACTIVE_STEP", payload: 2 });
  };;

 if (kycDetails) {
    return (
      <DigiLockerReview
        digiLockerData={kycDetails}
        roleName={roleName}
        onContinue={handleKycVerifiedContinue}
      />
    );
  }

  if (showAadhaarOcr) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <AdharOCR onSave={handleAadhaarSave} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 4, md: 8 },
        width: "100%",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "616px",
          maxWidth: "100%",
        }}>
        <Typography variant="h2" sx={{ color: "#333333" }}>
          {roleName ? `${roleName} Details` : "User Details"}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}>
          {!isInfluencerRole && (
            <TextField
              fullWidth
              required
              label="Name*"
              placeholder="Enter your name"
              value={userDetails?.name}
              onChange={handleNameChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          )}
          <TextField
            fullWidth
            required
            label="Mobile Number*"
            placeholder="Enter Mobile number"
            value={userDetails?.mobileNumber}
            onChange={handleMobileChange}
            inputProps={{
              maxLength: 10,
              pattern: "[0-9]*",
              inputMode: "numeric",
              readOnly: otpSent,
            }}
            error={!!errors.mobileNumber}
            helperText={errors.mobileNumber}
          />

          {otpSent && (
            <OTPVerificationComponent
              mobileNumber={userDetails.mobileNumber}
              otpLength={4}
              showText
              showMobileNumber
              initialTimer={60}
              onVerify={handleVerifyOtp}
              onResend={handleResendOtp}
              onAnimationComplete={handleOtpVerified}
              onError={(e) => console.error("Onboard buyer OTP error:", e)}
            />
          )}
        </Box>
      </Box>

      {!otpSent && (
        <Box sx={{ width: "616px", maxWidth: "100%" }}>
          <CustomButton
            label="Continue"
            fullWidth
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            onClick={handleContinue}
          />
        </Box>
      )}

      <HumbeeModal
        open={mrmPrompt.open}
        onClose={closeMrmPrompt}
        onConfirm={handleRoleAddition}
        loading={mrmSubmitting}
        title={`Continue as ${roleName}?`}
        subtitle={mrmPrompt.message}
        confirmLabel="Yes"
        cancelLabel="Cancel"
        illustration={<AddFirmSvg />}
      />

      <DigiLockerKycModal
        open={digiLockerOpen}
        onClose={() => setDigiLockerOpen(false)}
        mobileNumber={userDetails.mobileNumber}
        onContinue={handleKycContinue}
        loading={kycSubmitting}
      />
    </Box>
  );
};

export default UserDetailsStep;
